<template>
    <Card pageTitle="Email Templates">
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_EMAIL_TEMPLATE">
            <div class="template-library">
                <header class="library-header">
                    <div>
                        <p class="eyebrow">Customer engagement</p>
                        <h1>Email templates</h1>
                        <p class="intro">
                            Build and test global High Ties email designs. Draft changes do not affect live delivery until published; marketing templates additionally require an administrator.
                        </p>
                    </div>
                    <div class="header-actions">
                        <router-link class="automation-link" to="/settings/account-automation">
                            <span class="material-icons">tune</span>
                            Account automation
                        </router-link>
                        <button
                            v-if="canCreate"
                            type="button"
                            class="create-button"
                            @click="showGallery = true"
                        >
                            <span class="material-icons">add</span>
                            Create template
                        </button>
                    </div>
                </header>

                <div class="governance-strip" role="note">
                    <span class="material-icons">verified_user</span>
                    <div>
                        <strong>Safe publishing workflow</strong>
                        <span>Creators edit and test drafts. Publishing requires the dedicated publish permission; marketing publishing additionally requires an administrator.</span>
                    </div>
                </div>

                <div class="library-tabs" role="tablist" aria-label="Template library view">
                    <button
                        type="button"
                        :class="['library-tab', { active: !showArchived }]"
                        role="tab"
                        :aria-selected="!showArchived"
                        @click="setView(false)"
                    >
                        Active
                    </button>
                    <button
                        type="button"
                        :class="['library-tab', { active: showArchived }]"
                        role="tab"
                        :aria-selected="showArchived"
                        @click="setView(true)"
                    >
                        Archived
                    </button>
                </div>

                <div v-if="loading" class="template-list" aria-label="Loading email templates">
                    <div v-for="index in 4" :key="index" class="template-row skeleton-row">
                        <span class="skeleton skeleton-icon"></span>
                        <span class="skeleton skeleton-copy"></span>
                        <span class="skeleton skeleton-status"></span>
                    </div>
                </div>

                <div v-else-if="loadError" class="state-panel error-state" role="alert">
                    <span class="material-icons">error_outline</span>
                    <div>
                        <h2>Email templates could not be loaded</h2>
                        <p>{{ loadError }}</p>
                    </div>
                    <button type="button" class="secondary-action" @click="loadTemplates">Try again</button>
                </div>

                <div v-else class="template-list">
                    <article v-for="template in templates" :key="template.key" class="template-row">
                        <div class="template-icon" aria-hidden="true">
                            <span class="material-icons">{{ iconFor(template) }}</span>
                        </div>
                        <div class="template-copy">
                            <div class="title-line">
                                <h2>{{ template.label }}</h2>
                                <span class="audience-label">{{ template.audience }}</span>
                            </div>
                            <p>{{ template.description }}</p>
                            <div class="template-meta">
                                <span>{{ categoryLabel(template.category) }}</span>
                                <span v-if="template.publishedAt">Published {{ formatDate(template.publishedAt) }}</span>
                                <span v-else>Repository design active</span>
                                <span v-if="template.versionCount">{{ template.versionCount }} version{{ template.versionCount === 1 ? "" : "s" }}</span>
                            </div>
                        </div>
                        <div class="template-status">
                            <span :class="['status-pill', template.isPublished ? 'published' : 'fallback']">
                                {{ template.isPublished ? `Published v${template.publishedVersion}` : "Code fallback" }}
                            </span>
                            <span v-if="template.hasDraft" class="draft-indicator">
                                {{ template.draftUpdatedBy ? "Draft saved" : "Default design" }}
                            </span>
                        </div>
                        <router-link
                            :to="`/settings/email-templates/${template.key}`"
                            class="edit-link"
                            :aria-label="`${canEdit ? 'Edit' : 'View'} ${template.label}`"
                        >
                            {{ canEdit ? "Edit template" : "View template" }}
                            <span class="material-icons">arrow_forward</span>
                        </router-link>
                        <div class="row-actions">
                            <button
                                v-if="!showArchived && canDelete && !template.isSystem"
                                type="button"
                                class="row-action"
                                :disabled="busyKey === template.key"
                                :aria-label="`Archive ${template.label}`"
                                @click="archiveTemplate(template)"
                            >
                                <span class="material-icons">archive</span>
                            </button>
                            <button
                                v-else-if="showArchived && canDelete"
                                type="button"
                                class="row-action"
                                :disabled="busyKey === template.key"
                                :aria-label="`Restore ${template.label}`"
                                @click="restoreTemplate(template)"
                            >
                                <span class="material-icons">unarchive</span>
                            </button>
                        </div>
                    </article>
                    <div v-if="templates.length === 0" class="empty-state">
                        <span class="material-icons">{{ showArchived ? "archive" : "mail" }}</span>
                        <p>{{ showArchived ? "No archived templates." : "No templates yet — create your first one." }}</p>
                    </div>
                </div>
            </div>

            <StarterGalleryModal :open="showGallery" @close="showGallery = false" @created="onCreated" />
        </AllowPermission>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import Card from "../../components/UI/Card.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import StarterGalleryModal from "../../components/email-builder/StarterGalleryModal.vue"
import EmailTemplateFetchApi from "../../utils/fetch/email-template-fetch-api"
import { checkHasPermission, PERMISSION_CODES } from "../../store/permission"
import { confirmDialog, errMsg } from "../../utils/common"

export default {
    components: { Card, AllowPermission, StarterGalleryModal },
    data() {
        return {
            PERMISSION_CODES,
            templates: [],
            loading: true,
            loadError: "",
            showArchived: false,
            showGallery: false,
            busyKey: "",
            loadRequestId: 0,
        }
    },
    computed: {
        ...mapState("auth", ["user"]),
        canEdit() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_EDIT_EMAIL_TEMPLATE,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
        canCreate() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_CREATE_EMAIL_TEMPLATE,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
        canDelete() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_DELETE_EMAIL_TEMPLATE,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
    },
    mounted() {
        this.loadTemplates()
    },
    methods: {
        setView(showArchived) {
            this.showArchived = showArchived
            this.loadTemplates()
        },
        async loadTemplates() {
            const requestId = ++this.loadRequestId
            this.loading = true
            this.loadError = ""
            try {
                const templates = await EmailTemplateFetchApi.list({ includeArchived: this.showArchived })
                if (requestId === this.loadRequestId) this.templates = templates
            } catch (err) {
                if (requestId === this.loadRequestId) {
                    this.loadError = errMsg(err, "Check the connection and try again.")
                }
            }
            if (requestId === this.loadRequestId) this.loading = false
        },
        async archiveTemplate(template) {
            if (this.busyKey) return
            const confirmed = await confirmDialog({
                title: "Archive this email template?",
                text: "Archiving hides the template from the active library. Live delivery keeps using the last published version; restoring only makes the template editable again.",
                confirmButtonText: "Archive template",
            })
            if (!confirmed) return
            this.busyKey = template.key
            try {
                await EmailTemplateFetchApi.archive(template.key)
                this.$toast.success("Email template archived")
                await this.loadTemplates()
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not archive the email template"))
            } finally {
                this.busyKey = ""
            }
        },
        async restoreTemplate(template) {
            if (this.busyKey) return
            this.busyKey = template.key
            try {
                await EmailTemplateFetchApi.restore(template.key)
                this.$toast.success("Email template restored")
                await this.loadTemplates()
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not restore the email template"))
            } finally {
                this.busyKey = ""
            }
        },
        onCreated(template) {
            this.showGallery = false
            this.$router.push(`/settings/email-templates/${template.key}`)
        },
        iconFor(template) {
            const icons = {
                Customers: "person_add",
                Staff: "badge",
                "Compliance team": "gavel",
                "Report recipients": "summarize",
                "Inventory team": "inventory_2",
            }
            return icons[template.audience] || "mail"
        },
        categoryLabel(category) {
            return category === "operational" ? "Operational" : category === "marketing" ? "Marketing" : "Transactional"
        },
        formatDate(value) {
            const date = new Date(value)
            if (Number.isNaN(date.getTime())) return "recently"
            return new Intl.DateTimeFormat("en-CA", {
                month: "short",
                day: "numeric",
                year: "numeric",
            }).format(date)
        },
    },
}
</script>

<style lang="scss" scoped>
.template-library {
    color: #173b35;
}

.library-header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: end;
    gap: 28px;
    padding: 4px 2px 24px;
    border-bottom: 1px solid #dce5e1;
}

.eyebrow {
    margin: 0 0 7px;
    color: #9a6a32;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 1.8px;
    text-transform: uppercase;
}

h1 {
    margin: 0;
    color: #0f332d;
    font-size: clamp(27px, 3vw, 38px);
    font-weight: 750;
    letter-spacing: -0.8px;
}

.intro {
    max-width: 760px;
    margin: 9px 0 0;
    color: #60716b;
    font-size: 15px;
    line-height: 1.55;
}

.automation-link,
.edit-link,
.secondary-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 1px solid #cbd8d3;
    border-radius: 7px;
    background: #ffffff;
    color: #173b35;
    font-weight: 750;
    text-decoration: none;
    transition: transform 160ms ease, border-color 160ms ease, background-color 160ms ease;
}

.automation-link {
    min-height: 42px;
    padding: 0 16px;
}

.automation-link:hover,
.edit-link:hover,
.secondary-action:hover {
    border-color: #77988e;
    background: #f5f9f7;
}

.automation-link:active,
.edit-link:active,
.secondary-action:active {
    transform: translateY(1px);
}

.automation-link .material-icons,
.edit-link .material-icons {
    font-size: 18px;
}

.governance-strip {
    display: grid;
    grid-template-columns: 34px minmax(0, 1fr);
    gap: 12px;
    margin: 20px 0 8px;
    padding: 14px 16px;
    border-left: 4px solid #b8864c;
    background: #f7f3ec;
    color: #53655f;
}

.governance-strip > .material-icons {
    color: #9a6a32;
}

.governance-strip strong,
.governance-strip span {
    display: block;
}

.governance-strip strong {
    margin-bottom: 2px;
    color: #173b35;
}

.governance-strip span {
    font-size: 13px;
    line-height: 1.45;
}

.template-list {
    margin-top: 8px;
    border-top: 1px solid #dce5e1;
}

.template-row {
    display: grid;
    grid-template-columns: 46px minmax(280px, 1fr) minmax(142px, auto) auto;
    align-items: center;
    gap: 18px;
    padding: 20px 6px;
    border-bottom: 1px solid #e4ebe8;
}

.template-row:hover {
    background: #fbfcfb;
}

.template-icon {
    display: grid;
    width: 42px;
    height: 42px;
    place-items: center;
    border: 1px solid #d5e0dc;
    border-radius: 8px;
    background: #f4f8f6;
    color: #1d5a4d;
}

.template-icon .material-icons {
    font-size: 21px;
}

.title-line {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 9px;
}

.template-copy h2,
.state-panel h2 {
    margin: 0;
    color: #173b35;
    font-size: 17px;
    font-weight: 780;
}

.template-copy p,
.state-panel p {
    margin: 5px 0 0;
    color: #65766f;
    font-size: 13px;
    line-height: 1.45;
}

.audience-label {
    padding: 3px 7px;
    border-radius: 999px;
    background: #edf3f0;
    color: #51665f;
    font-size: 11px;
    font-weight: 750;
}

.template-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 7px;
    color: #7a8984;
    font-size: 11px;
}

.template-meta span + span::before {
    content: "";
    display: inline-block;
    width: 3px;
    height: 3px;
    margin: 0 8px 2px 0;
    border-radius: 50%;
    background: #afbbb7;
}

.template-status {
    display: flex;
    align-items: flex-end;
    flex-direction: column;
    gap: 5px;
}

.status-pill {
    display: inline-flex;
    padding: 5px 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 800;
}

.status-pill.published {
    background: #e2f1eb;
    color: #236047;
}

.status-pill.fallback {
    background: #eef0f1;
    color: #5e6864;
}

.draft-indicator {
    color: #9a6a32;
    font-size: 11px;
    font-weight: 700;
}

.edit-link {
    min-height: 39px;
    padding: 0 13px;
    white-space: nowrap;
    font-size: 13px;
}

.state-panel {
    display: grid;
    grid-template-columns: 40px minmax(0, 1fr) auto;
    align-items: center;
    gap: 15px;
    margin-top: 20px;
    padding: 22px;
    border: 1px solid #e6c9c2;
    background: #fff8f6;
}

.state-panel > .material-icons {
    color: #a34b3e;
    font-size: 28px;
}

.secondary-action {
    min-height: 38px;
    padding: 0 14px;
}

.skeleton-row {
    pointer-events: none;
}

.skeleton {
    display: block;
    border-radius: 6px;
    background: linear-gradient(90deg, #edf2f0 20%, #f7f9f8 50%, #edf2f0 80%);
    background-size: 220% 100%;
    animation: shimmer 1.4s ease-in-out infinite;
}

.skeleton-icon { width: 42px; height: 42px; }
.skeleton-copy { width: min(420px, 100%); height: 42px; }
.skeleton-status { width: 110px; height: 26px; }

.header-actions {
    display: flex;
    align-items: center;
    gap: 10px;
}

.create-button {
    display: inline-flex;
    min-height: 42px;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 0 16px;
    border: 1px solid #123b34;
    border-radius: 7px;
    background: #123b34;
    color: #fff;
    font-weight: 750;
    cursor: pointer;
    transition: transform 160ms ease, background-color 160ms ease;
}

.create-button:hover {
    background: #0a2d27;
}

.create-button:active {
    transform: translateY(1px);
}

.create-button .material-icons {
    font-size: 18px;
}

.library-tabs {
    display: flex;
    gap: 4px;
    margin: 14px 0 4px;
    padding: 3px;
    width: fit-content;
    border: 1px solid #dce5e1;
    border-radius: 9px;
    background: #f4f8f6;
}

.library-tab {
    padding: 7px 16px;
    border: 0;
    border-radius: 7px;
    background: transparent;
    color: #5c716a;
    font-size: 13px;
    font-weight: 750;
    cursor: pointer;
}

.library-tab.active {
    background: #fff;
    color: #173b35;
    box-shadow: 0 1px 3px rgba(20, 55, 47, 0.16);
}

.row-actions {
    display: flex;
    align-items: center;
    gap: 6px;
}

.row-action {
    display: grid;
    width: 36px;
    height: 36px;
    place-items: center;
    border: 1px solid #d8e2de;
    border-radius: 7px;
    background: #fff;
    color: #5c716a;
    cursor: pointer;
}

.row-action:hover:not(:disabled) {
    border-color: #8faca3;
    background: #f3f8f6;
}

.row-action:disabled {
    cursor: not-allowed;
    opacity: 0.5;
}

.row-action .material-icons {
    font-size: 18px;
}

.empty-state {
    display: grid;
    place-items: center;
    gap: 8px;
    padding: 44px 20px;
    color: #7a8984;
    font-size: 13px;
}

.empty-state .material-icons {
    color: #a8b6b1;
    font-size: 30px;
}

@keyframes shimmer {
    to { background-position: -220% 0; }
}

@media (max-width: 1000px) {
    .template-row {
        grid-template-columns: 46px minmax(0, 1fr) auto;
    }
    .template-status {
        display: none;
    }
}

@media (max-width: 720px) {
    .library-header,
    .template-row,
    .state-panel {
        grid-template-columns: 1fr;
    }
    .automation-link,
    .edit-link {
        width: 100%;
    }
    .template-icon {
        display: none;
    }
}
</style>
